/**
 * Survey realtime service — multi-surveyor finding sync.
 *
 * Uses Supabase Realtime to subscribe to survey_findings changes
 * for the active survey, enabling two surveyors to see each other's
 * findings in near-real-time.
 */

import { realtime } from '../supabase'
import type { SurveyDomain, FacilityArea } from './survey'

// ── Types ───────────────────────────────────────────────────────────────────

export interface RealtimeFinding {
  finding_id: string
  domain: SurveyDomain
  area: FacilityArea
  severity: number
  observation: string
  surveyor_name: string | null
  created_at: string
}

export interface SurveyRealtimeCallbacks {
  onFindingAdded: (finding: RealtimeFinding) => void
  onError?: (error: Error) => void
}

// ── Subscription ────────────────────────────────────────────────────────────

type RealtimeChannel = ReturnType<typeof realtime.channel>
let activeChannel: RealtimeChannel | null = null

/**
 * Subscribe to real-time finding updates for a survey.
 * Returns an unsubscribe function.
 */
export function subscribeToSurveyFindings(
  surveyId: string,
  callbacks: SurveyRealtimeCallbacks,
): () => void {
  if (activeChannel) {
    realtime.removeChannel(activeChannel)
    activeChannel = null
  }

  const channel = realtime
    .channel(`survey-findings-${surveyId}`)
    .on(
      'postgres_changes' as any,
      {
        event: 'INSERT',
        schema: 'public',
        table: 'survey_findings',
        filter: `survey_id=eq.${surveyId}`,
      },
      (payload: any) => {
        const row = payload.new as Record<string, unknown>
        callbacks.onFindingAdded({
          finding_id: String(row.finding_id ?? ''),
          domain: String(row.domain ?? 'ENV') as SurveyDomain,
          area: String(row.area ?? 'Other') as FacilityArea,
          severity: Number(row.severity ?? 1),
          observation: String(row.observation ?? ''),
          surveyor_name: row.surveyor_name ? String(row.surveyor_name) : null,
          created_at: String(row.created_at ?? new Date().toISOString()),
        })
      },
    )
    .subscribe((status: string, err?: Error) => {
      if (err) {
        callbacks.onError?.(new Error(`Realtime subscription error: ${err.message}`))
      }
    })

  activeChannel = channel

  return () => {
    realtime.removeChannel(channel)
    if (activeChannel === channel) activeChannel = null
  }
}
